import { Button, Col, Row, Table } from "antd";
import { FormInstance } from "antd/lib/form";
import { AxiosResponse } from "axios";
import moment from "moment";
import React, { Fragment, RefObject } from "react";
import BaseForm from "../components/Form/BaseForm";
import ModalCreator from "../components/ModalCreator";
import entitlements from "../config/entitlements";
import {
  FormFieldConfig,
  InteractionMode,
  OpportunityInteraction,
  SelectOptionsInterface,
} from "../helpers/shared-interfaces";
import { request } from "../helpers/utilities";
import EntitledSection from "./EntitledSection";

interface Props {
  interactions: OpportunityInteraction[];
  createInteraction: () => Promise<void>;
  reference: RefObject<FormInstance>;
  showForm: boolean;
  showBorder?: boolean;
  showHeading?: boolean;
}

interface State {
  interactionModes: InteractionMode[];
  showCreateInteractionModal: boolean;
}

const GET_INTERACTION_MODES = `
query {
  interactionModes {
    id
    name
  }
}
`;

export const generateInteractionFormFields = ({
  interactionModes,
}: {
  interactionModes: SelectOptionsInterface[];
}): FormFieldConfig[] => {
  return [
    {
      id: "interactionDate",
      name: "interactionDate",
      inputType: "defaultDate",
      rule: "object",
      required: false,
      label: "Interaction Date",
      elementStyle: { width: "100%" },
      colConfig: {
        span: 24,
      },
    },
    {
      id: "description",
      name: "description",
      required: true,
      inputType: "string",
      rule: "string",
      label: "Description",
      placeholder: "Enter description",
      colConfig: {
        span: 24,
      },
    },
    {
      id: "interactionModeId",
      name: "interactionModeId",
      placeholder: "Interaction Mode",
      required: true,
      inputType: "select",
      rule: "string",
      label: "Interaction Mode",

      selectOptions: interactionModes,
      elementStyle: {
        width: "100%",
      },
      colConfig: {
        span: 24,
      },
    },
  ];
};

export const columns = [
  {
    title: "Interaction Date",
    dataIndex: "interactionDate",
    key: "Interaction Date",
    width: "20%",
    render(text: string, record: OpportunityInteraction): JSX.Element {
      return (
        <span>
          {record.interactionDate
            ? moment(record.interactionDate, "YYYY-MM-DD HH:mm:ss").format("llll")
            : "N/A"}
        </span>
      );
    },
  },
  {
    title: "Description",
    dataIndex: "description",
    key: "Description",
    width: "50%",
  },
  {
    title: "Interaction Mode",
    dataIndex: ["interactionMode", "name"],
    key: "Interaction Mode",
    width: "30%",
  },
];

export function InteractionTableFactory(
  data: OpportunityInteraction[],
): JSX.Element {
  const tempData: OpportunityInteraction[] = data.map(
    (interaction: OpportunityInteraction): OpportunityInteraction => {
      return { ...interaction, key: interaction.id };
    },
  );
  return (
    <Table
      bordered={true}
      columns={columns}
      dataSource={tempData}
      pagination={false}
    />
  );
}

export default class InteractionsSection extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { interactionModes: [], showCreateInteractionModal: false };
  }

  getInteractionModes = (): void => {
    request({
      query: GET_INTERACTION_MODES,
      variables: {},
    }).then(
      ({
        data,
      }: AxiosResponse<{
        data: {
          interactionModes: InteractionMode[];
        };
      }>): void => {
        this.setState({ interactionModes: data.data.interactionModes });
      },
    );
  };

  componentDidMount = (): void => {
    this.getInteractionModes();
  };

  componentDidUpdate(prevProps: Props): void {
    if (
      this.state.showCreateInteractionModal &&
      this.props.interactions.length > prevProps.interactions.length
    ) {
      this.closeCreateInteractionModal();
    }
  }

  openCreateInteractionModal = (): void => {
    this.setState({ showCreateInteractionModal: true });
  };

  closeCreateInteractionModal = (): void => {
    this.setState({ showCreateInteractionModal: false });
    this.props.reference.current?.resetFields();
  };

  render(): JSX.Element {
    const interactionFields = generateInteractionFormFields({
      interactionModes: this.state.interactionModes,
    });

    return (
      <Fragment>
        <div
          style={
            this.props.showBorder
              ? {
                  border: "1px solid rgb(232 232 232)",
                  padding: "20px",
                  boxSizing: "border-box",
                }
              : undefined
          }
        >
          {this.props.showForm && (
            <Fragment>
              <Row style={{ marginBottom: 16 }}>
                <EntitledSection
                  entitlement={
                    entitlements.BusinessDevelopment.interactions.create
                  }
                >
                  <Button className="base-button" onClick={this.openCreateInteractionModal}>
                    Add Interaction
                  </Button>
                </EntitledSection>
              </Row>
              {ModalCreator({
                title: "Add Interaction",
                okType: "primary",
                okText: "Add Interaction",
                showModal: this.state.showCreateInteractionModal,
                onOkay: this.props.createInteraction,
                toggleModal: this.closeCreateInteractionModal,
                width: 560,
                inscription: (
                  <BaseForm
                    layout="vertical"
                    reference={this.props.reference}
                    fields={interactionFields}
                    fieldsToHide={[]}
                  />
                ),
              })}
              <Row>
                <Col span={24}>
                <EntitledSection
                  entitlement={
                    entitlements.BusinessDevelopment.interactions.list
                  }
                >
                  {InteractionTableFactory(this.props.interactions)}
                </EntitledSection>
                </Col>
              </Row>
            </Fragment>
          )}
          {!this.props.showForm && (
            <Row>
              <Col span={24}>
                <EntitledSection
                  entitlement={
                    entitlements.BusinessDevelopment.interactions.list
                  }
                >
                  {InteractionTableFactory(this.props.interactions)}
                </EntitledSection>
              </Col>
            </Row>
          )}
        </div>
      </Fragment>
    );
  }
}
